import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, animate } from 'framer-motion';
import { ModelsPage } from './ModelsPage';
import { RankingsSection } from './RankingsSection';
import { CreditsSection, PlansSection, UsageSection } from './CreditSections';
import { GallerySection } from './GallerySection';
import { useAuth } from '../contexts/AuthContext';
import mainLogo from '../assets/DAGGPT-01.png';
import SentinelNodes from './SentinelNodes';
import SentinelHost from './SentinelHost';
import MyPodsDashboard from './MyPodsDashboard';
import { WalletPage } from './WalletPage';
import { ProfileSection } from './ProfileSection';
import InvestorDashboard from './InvestorDashboard';

const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : 'https://api.daggpt.network';

// Smart DGCC formatter - shows enough decimals to be meaningful (up to 8)
function fmtDgcc(n) {
  const v = parseFloat(n ?? 0);
  if (v === 0) return '0';
  if (Math.abs(v) >= 100)  return v.toFixed(2);
  if (Math.abs(v) >= 1)    return v.toFixed(4);
  if (Math.abs(v) >= 0.0001) return v.toFixed(6);
  return v.toFixed(8);
}

// ─── Design tokens (matches CreditSections) ─────────────────────
export const T = {
  bg:          '#ffffff',
  shadow:      '8px 8px 18px #cccce6, -8px -8px 18px #ffffff',
  shadowSm:    '5px 5px 12px #cccce6, -5px -5px 12px #ffffff',
  shadowInset: 'inset 4px 4px 10px #cccce6, inset -4px -4px 10px #ffffff',
  purple:      '#7c3aed',
  purpleMid:   '#6d28d9',
  purpleLight: '#ede9fe',
  purpleFaint: '#f5f3ff',
  textPrimary: '#1e1b4b',
  textSecond:  '#5b5e8b',
  textMuted:   '#9ca3b8',
  border:      'rgba(124,58,237,0.10)',
  sidebar:     '#ffffff',
};

// ─── Inline SVG icon library ─────────────────────────────────────
export const ICONS = {
  dashboard:  'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  models:     'M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18',
  rankings:   'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  usage:      'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  buy:        'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
  dgcc:       'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  gallery:    'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  logout:     'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
  chat:       'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  image:      'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  video:      'M15 10l4.553-2.276A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z',
  music:      'M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3',
  website:    'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9',
  user:       'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  star:       'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
  zap:        'M13 10V3L4 14h7v7l9-11h-7z',
  clock:      'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  shield:     'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  wallet:     'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
  arrow:      'M13 7l5 5m0 0l-5 5m5-5H6',
  server:     'M4 7a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V7zm0 10a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2z',
};

export function SvgIcon({ name, size = 18, color = T.purple, strokeWidth = 1.75 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d={ICONS[name] || ICONS.dashboard} />
    </svg>
  );
}

// ─── Neumorphic card ─────────────────────────────────────────────
function NmCard({ children, style = {}, onClick, hover = true }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div onClick={onClick}
      onMouseEnter={() => hover && setHovered(true)}
      onMouseLeave={() => hover && setHovered(false)}
      style={{
        background: T.bg, borderRadius: 20,
        boxShadow: hovered && onClick ? T.shadow : T.shadowSm,
        transform: hovered && onClick ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}>
      {children}
    </div>
  );
}

// ─── Nav items config ────────────────────────────────────────────
const NAV_ITEMS = [
  { id: 'introduction', label: 'Dashboard',  icon: 'dashboard' },
  { id: 'profile',      label: 'My Profile', icon: 'user'      },
  { 
    id: 'sentinel',     
    label: 'Sentinel Nodes', 
    icon: 'server',
    subItems: [
      { id: 'sentinel-host', label: 'Host A GPU' },
      { id: 'sentinel-rent', label: 'Rent A GPU' },
      { id: 'sentinel-pods', label: 'My Pods' },
      { id: 'investor-dashboard', label: 'Investor Dashboard' }
    ]
  },
  { id: 'atelier',      label: 'Unified Workspace', icon: 'chat', href: '/workspace' },
  { id: 'models',       label: 'Models',     icon: 'models'    },
  { id: 'rankings',     label: 'Rankings',   icon: 'rankings'  },
  { id: 'usage',        label: 'Analytics',  icon: 'usage'     },
  { id: 'plans',        label: 'Get DGCC',   icon: 'buy'       },
  { id: 'dgcc',         label: 'Usage',      icon: 'dgcc'      },
  // { id: 'wallet',       label: 'Wallet',     icon: 'wallet'    },
  { id: 'gallery',      label: 'Gallery',    icon: 'gallery'   },
];

// ─── Quick action cards ──────────────────────────────────────────
const QUICK_ACTIONS = [
  { label: 'AI Chat',         desc: 'Multi-model conversations', icon: 'chat',    href: '/chat'              },
  { label: 'Image Generator', desc: 'Text & image-to-image',     icon: 'image',   href: '/image-generation'  },
  { label: 'Video Generator', desc: 'Text & image-to-video',     icon: 'video',   href: '/video-generation'  },
  { label: 'Music Generator', desc: 'AI music composition',      icon: 'music',   href: '/music-generation'  },
  { label: 'Website Builder', desc: 'AI-powered web creation',   icon: 'website', href: 'https://builder.daggpt.network'   },
];

// ─── Featured models ─────────────────────────────────────────────
const MODELS = [
  { name: 'Gemini 2.5 Pro',    provider: 'Google DeepMind', badge: 'Reasoning',      icon: 'zap'    },
  { name: 'Claude 4.5',        provider: 'Anthropic',       badge: 'Analysis',       icon: 'shield' },
  { name: 'Imagen 4',          provider: 'Google',          badge: 'Image Gen',      icon: 'image'  },
  { name: 'Veo 3.1',           provider: 'Google',          badge: 'Video Gen',      icon: 'video'  },
  { name: 'Lyria 2',           provider: 'Google',          badge: 'Music Gen',      icon: 'music'  },
  { name: 'DeepSeek 3.2',      provider: 'DeepSeek AI',     badge: 'Code',           icon: 'models' },
];

// ─── Staking Options ─────────────────────────────────────────────
const STAKING_OPTIONS = [
  { duration: 1, apy: 12, label: '1 Year',  sublabel: '12% APY' },
  { duration: 2, apy: 18, label: '2 Years', sublabel: '18% APY' },
  { duration: 3, apy: 24, label: '3 Years', sublabel: '24% APY' },
];

// ─── Staking Modal Design System (Dribbble-grade premium white) ──
const SPM = {
  bg: '#FFFFFF', canvas: '#FAFAF7', ink: '#0A0A0A', muted: '#71717A',
  subtle: '#A1A1AA', line: '#EAEAE6', lineSoft: '#F2F1EC',
  accentA: '#059669', accentB: '#34D399',
  accentGrad: 'linear-gradient(135deg, #059669 0%, #34D399 100%)',
  accentTint: '#ECFDF5', ok: '#10B981',
};
const SPM_SERIF = '"Nasalization", "Instrument Serif", Georgia, serif';
const SPM_MONO  = '"JetBrains Mono", ui-monospace, Menlo, monospace';

// Smart DGCC formatter - decimals for small values, rounded for large
const spmFmt = (n) => {
  const num = Number(n) || 0;
  if (num === 0) return '0';
  if (Math.abs(num) >= 100) return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (Math.abs(num) >= 1)   return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return num.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

// Count-up number (spring to target)
function SpmCountUp({ to, duration = 1.1, format = (n) => Math.round(n).toLocaleString() }) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const controls = animate(0, Number(to) || 0, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => { node.textContent = format(v); },
    });
    return () => controls.stop();
  }, [to, duration, format]);
  return <span ref={ref}>{format(0)}</span>;
}

// Ambient drifting background orbs
function SpmAmbientOrbs() {
  return (
    <div aria-hidden style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', borderRadius: 'inherit' }}>
      {[
        { bg: '#FFE4CC', size: 420, x: '-12%', y: '-18%', delay: 0 },
        { bg: '#FFD6E8', size: 380, x: '75%',  y: '-10%', delay: 2 },
        { bg: '#E8E2FF', size: 340, x: '30%',  y: '80%',  delay: 4 },
      ].map((o, i) => (
        <motion.div key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6, x: [0, 20, 0, -20, 0], y: [0, -15, 0, 15, 0] }}
          transition={{
            opacity: { duration: 1.2, delay: 0.1 * i },
            x: { duration: 18 + i * 2, repeat: Infinity, ease: 'easeInOut', delay: o.delay },
            y: { duration: 22 + i * 2, repeat: Infinity, ease: 'easeInOut', delay: o.delay },
          }}
          style={{ position: 'absolute', left: o.x, top: o.y, width: o.size, height: o.size, borderRadius: '50%', background: o.bg, filter: 'blur(80px)' }}
        />
      ))}
    </div>
  );
}

// Stylized SVG yield curve
function SpmYieldCurve() {
  return (
    <svg viewBox="0 0 200 120" fill="none" style={{ width: '100%', height: 'auto', display: 'block' }}>
      <defs>
        <linearGradient id="spm-stroke" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={SPM.accentA} />
          <stop offset="100%" stopColor={SPM.accentB} />
        </linearGradient>
        <linearGradient id="spm-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={SPM.accentA} stopOpacity="0.18" />
          <stop offset="100%" stopColor={SPM.accentB} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[30, 60, 90].map(y => (
        <line key={y} x1="0" y1={y} x2="200" y2={y} stroke={SPM.line} strokeDasharray="2 4" strokeWidth="0.5" />
      ))}
      <motion.path d="M 10 100 Q 70 92 100 72 T 190 18 L 190 110 L 10 110 Z" fill="url(#spm-fill)"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.8 }} />
      <motion.path d="M 10 100 Q 70 92 100 72 T 190 18" stroke="url(#spm-stroke)" strokeWidth="2.5" strokeLinecap="round" fill="none"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.2 }} />
      {[
        { cx: 70,  cy: 90, apy: '12%' },
        { cx: 130, cy: 60, apy: '18%' },
        { cx: 190, cy: 18, apy: '24%' },
      ].map((p, i) => (
        <motion.g key={i}
          initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6 + i * 0.12, type: 'spring', stiffness: 260, damping: 18 }}>
          <circle cx={p.cx} cy={p.cy} r="5" fill="#fff" stroke="url(#spm-stroke)" strokeWidth="2" />
          <text x={p.cx} y={p.cy - 10} textAnchor="middle" fill={SPM.muted} fontSize="9" fontWeight="600" fontFamily={SPM_MONO}>
            {p.apy}
          </text>
        </motion.g>
      ))}
    </svg>
  );
}

export function DashboardPage() {
  const [activeSection, setActiveSection] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('section') || 'introduction';
    }
    return 'introduction';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedNav, setExpandedNav] = useState('sentinel');
  const { userProfile, user, logout, dgccBalance, refreshBalance } = useAuth();
  
  useEffect(() => {
    if (userProfile && userProfile.profile_completed === false) {
      setActiveSection('profile');
    }
  }, [userProfile]);

  const handleLogout = async () => {
    localStorage.removeItem('daggpt_token');
    localStorage.removeItem('daggpt_user');
    localStorage.removeItem('daggpt_authenticated');
    if (logout) {
      logout().catch(() => {});
    }
    window.location.href = '/';
  };
  const [greeting,    setGreeting]    = useState('');
  const [currentTime, setCurrentTime] = useState(null);
  const [dgccData,    setDgccData]    = useState(null);
  const [mounted,     setMounted]     = useState(false);

  const [scale, setScale] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
      const designWidth = 1440; // Optimal dashboard target width
      if (window.innerWidth <= 1024) {
        setScale(1); // Let responsive CSS handle mobile/tablet
      } else if (window.innerWidth < designWidth) {
        const ratio = window.innerWidth / designWidth;
        setScale(Math.max(0.60, ratio));
      } else {
        setScale(1);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Staking perk modal state (post-Stripe DGCC purchase)
  const [showStakingModal, setShowStakingModal] = useState(false);
  const [stakingStep, setStakingStep]   = useState(1);  // 1 = congrats, 2 = duration picker
  const [stakingDgcc, setStakingDgcc]   = useState(0);
  const [stakingSessionId, setStakingSessionId] = useState(null);
  const [stakingSelected, setStakingSelected] = useState(null);  // { duration, apy, label, sublabel }
  const [stakingSubmitting, setStakingSubmitting] = useState(false);
  const [stakingError, setStakingError] = useState(null);
  const [stakingDone, setStakingDone]   = useState(false);

  useEffect(() => {
    const h = new Date().getHours();
    setGreeting(h < 12 ? 'Good Morning' : h < 18 ? 'Good Afternoon' : 'Good Evening');
    setCurrentTime(new Date());
    const clock = setInterval(() => setCurrentTime(new Date()), 1000);
    setTimeout(() => setMounted(true), 80);
    return () => clearInterval(clock);
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    refreshBalance(user.id);
  }, [user?.id, refreshBalance]);

  // Sync when global balance refreshes (e.g. after chat generation)
  useEffect(() => { if (dgccBalance) setDgccData(dgccBalance); }, [dgccBalance]);

  // Detect Stripe DGCC purchase success via URL params
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const section = params.get('section');
    const sessionId = params.get('session_id');
    if (payment === 'success' && section === 'credits' && sessionId && user?.id) {
      // Verify with backend and get DGCC amount
      fetch(`${API_URL}/api/billing/verify-session?session_id=${sessionId}`, {
        headers: { 'x-user-id': user.id }
      })
        .then(r => r.json())
        .then(data => {
          if (data.success && data.paid) {
            setStakingDgcc(data.dgccAmount || 0);
            setStakingSessionId(sessionId);
            setShowStakingModal(true);
            setStakingStep(1);
            // Clean URL
            window.history.replaceState({}, '', '/dashboard?section=credits');
          }
        })
        .catch(() => {});
    }
  }, [user?.id]);

  // ─── Sidebar ───────────────────────────────────────────────────
  const renderSidebar = () => (
    <>
      {mobileMenuOpen && (
        <div 
          className="dashboard-mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(30, 27, 75, 0.4)', backdropFilter: 'blur(4px)', zIndex: 90 }}
        />
      )}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'open' : ''}`} style={{
        width: 248, background: T.sidebar,
        borderRight: `1px solid ${T.border}`,
        display: 'flex', flexDirection: 'column',
        position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 100,
      }}>
      {/* Logo */}
      <div style={{ padding: '24px 24px 20px', borderBottom: `1px solid ${T.border}` }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
          <img src={mainLogo} alt="DAGGPT" style={{ height: 38, width: 'auto', objectFit: 'contain' }} />
        </a>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '14px 12px', overflowY: 'auto' }}>
        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.4, color: T.textMuted, textTransform: 'uppercase', padding: '0 10px 10px' }}>Navigation</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {NAV_ITEMS.map(item => {
            const hasSubItems = !!item.subItems;
            const isExpanded = expandedNav === item.id;
            const isActiveParent = activeSection === item.id || (hasSubItems && item.subItems.some(sub => sub.id === activeSection));
            const active = !hasSubItems && activeSection === item.id;

            return (
              <div key={item.id} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <button onClick={() => {
                  if (item.href) {
                    window.location.href = item.href;
                  } else if (hasSubItems) {
                    setExpandedNav(isExpanded ? null : item.id);
                  } else {
                    setActiveSection(item.id);
                    setMobileMenuOpen(false);
                  }
                }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 11,
                    padding: '10px 14px', borderRadius: 12, border: 'none', width: '100%',
                    background: active ? `linear-gradient(135deg, ${T.purple}, ${T.purpleMid})` : 'transparent',
                    color: active ? '#fff' : (isActiveParent ? T.purple : T.textSecond),
                    fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                    boxShadow: active ? `0 4px 14px rgba(124,58,237,0.28)` : 'none',
                    transition: 'all 0.16s',
                  }}
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.background = T.purpleLight; e.currentTarget.style.color = T.purple; } }}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isActiveParent ? T.purple : T.textSecond; } }}>
                  <SvgIcon name={item.icon} size={16} color={active ? '#fff' : (isActiveParent ? T.purple : T.textSecond)} strokeWidth={active ? 2 : 1.75} />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {hasSubItems && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  )}
                </button>
                
                {hasSubItems && (
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div 
                        initial={{ height: 0, opacity: 0 }} 
                        animate={{ height: 'auto', opacity: 1 }} 
                        exit={{ height: 0, opacity: 0 }}
                        style={{ overflow: 'hidden', paddingLeft: 32, display: 'flex', flexDirection: 'column', gap: 2 }}
                      >
                        {item.subItems.filter(sub => sub.id !== 'investor-dashboard' || (userProfile?.is_investor || user?.is_investor)).map(sub => {
                          const isSubActive = activeSection === sub.id;
                          return (
                            <button key={sub.id} onClick={() => {
                              setActiveSection(sub.id);
                              setMobileMenuOpen(false);
                            }}
                            style={{
                              display: 'flex', alignItems: 'center',
                              padding: '8px 12px', borderRadius: 8, border: 'none', width: '100%',
                              background: isSubActive ? T.purpleLight : 'transparent',
                              color: isSubActive ? T.purple : T.textSecond,
                              fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                              transition: 'all 0.16s',
                            }}
                            onMouseEnter={e => { if (!isSubActive) { e.currentTarget.style.background = 'rgba(124,58,237,0.05)'; e.currentTarget.style.color = T.purple; } }}
                            onMouseLeave={e => { if (!isSubActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.textSecond; } }}>
                              {sub.label}
                            </button>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            );
          })}
        </div>

        {/* DGCC balance pill in sidebar */}
        {dgccData !== null && (
          <div style={{ marginTop: 20, padding: '0 4px' }}>
            <div style={{ background: T.bg, borderRadius: 14, padding: '14px 16px', boxShadow: `inset 2px 2px 6px #d0d0e8, inset -2px -2px 6px rgba(255,255,255,0.7)` }}>
              <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.2, color: T.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>DGCC Balance</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: T.purple, letterSpacing: -0.5 }}>{fmtDgcc(dgccData?.balance)}</div>
              <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>DGCC Coins</div>
              <div style={{ marginTop: 10, height: 3, background: T.purpleLight, borderRadius: 100, overflow: 'hidden' }}>
                <div style={{ width: '60%', height: '100%', background: `linear-gradient(90deg, ${T.purple}, ${T.purpleMid})`, borderRadius: 100 }} />
              </div>
            </div>
          </div>
        )}

        {/* Web3 Connect Wallet Button */}
        <div style={{ marginTop: 20, padding: '0 16px', display: 'flex', justifyContent: 'center' }}>
          <appkit-button balance="hide" />
        </div>
      </nav>

      {/* Logout */}
      <div style={{ padding: '12px', borderTop: `1px solid ${T.border}` }}>
        <button onClick={handleLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: 11, width: '100%',
            padding: '10px 14px', borderRadius: 12, border: 'none',
            background: 'transparent', color: '#ef4444',
            fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.16s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <SvgIcon name="logout" size={16} color="#ef4444" />
          Sign out
        </button>
      </div>
    </aside>
    </>
  );

  // ─── Dashboard home content ────────────────────────────────────
  const renderDashboardHome = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>

      {/* ── Row 1: Stat cards ── */}
      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 18 }}>
        {/* DGCC Balance */}
        <NmCard onClick={() => setActiveSection('dgcc')} style={{ padding: '20px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ width: 38, height: 38, borderRadius: 11, background: `linear-gradient(135deg, ${T.purple}, ${T.purpleMid})`, boxShadow: `0 5px 14px rgba(124,58,237,0.33)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SvgIcon name="dgcc" size={16} color="#fff" />
            </div>
            <SvgIcon name="arrow" size={14} color={T.textMuted} />
          </div>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.2, color: T.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>DGCC Balance</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.textPrimary, letterSpacing: -0.5 }}>
            {dgccData !== null ? fmtDgcc(dgccData.balance) : '-'}
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>≈ ${(+(dgccData?.balance ?? 0)).toFixed(4)} USD</div>
        </NmCard>

        {/* Total Spent */}
        <NmCard style={{ padding: '20px 22px' }}>
          <div style={{ width: 38, height: 38, borderRadius: 11, background: T.bg, boxShadow: T.shadowSm, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
            <SvgIcon name="zap" size={16} color={T.purple} />
          </div>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.2, color: T.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Total Spent</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.textPrimary, letterSpacing: -0.5 }}>
            {dgccData !== null ? fmtDgcc(dgccData.total_spent) : '-'}
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>DGCC consumed</div>
        </NmCard>

        {/* Account */}
        <NmCard style={{ padding: '20px 22px' }}>
          <div style={{ width: 38, height: 38, borderRadius: 11, background: T.bg, boxShadow: T.shadowSm, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
            <SvgIcon name="shield" size={16} color={T.purple} />
          </div>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.2, color: T.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Account Tier</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.textPrimary, letterSpacing: -0.5 }}>
            {userProfile?.isPremium ? 'Premium' : 'Free'}
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>Current plan</div>
        </NmCard>

        {/* Bonus received */}
        <NmCard style={{ padding: '20px 22px' }}>
          <div style={{ width: 38, height: 38, borderRadius: 11, background: T.bg, boxShadow: T.shadowSm, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
            <SvgIcon name="star" size={16} color={T.purple} />
          </div>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.2, color: T.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Bonus Received</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.textPrimary, letterSpacing: -0.5 }}>
            {dgccData !== null ? fmtDgcc(dgccData.total_bonus) : '-'}
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>DGCC free grants</div>
        </NmCard>
      </div>

      {/* ── Row 2: Quick Actions ── */}
      <NmCard hover={false} style={{ padding: '24px 26px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, letterSpacing: -0.2 }}>Quick Launch</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>Jump directly into any AI workspace</div>
          </div>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: T.bg, boxShadow: T.shadowSm, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SvgIcon name="zap" size={15} color={T.purple} />
          </div>
        </div>
        <div className="quick-actions-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14 }}>
          {QUICK_ACTIONS.map(action => (
            <div key={action.label}
              onClick={() => window.location.href = action.href}
              style={{ cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.querySelector('.inner').style.boxShadow = T.shadow; e.currentTarget.querySelector('.inner').style.transform = 'translateY(-3px)'; }}
              onMouseLeave={e => { e.currentTarget.querySelector('.inner').style.boxShadow = T.shadowSm; e.currentTarget.querySelector('.inner').style.transform = 'translateY(0)'; }}>
              <div className="inner" style={{ background: T.bg, borderRadius: 16, padding: '18px 14px', boxShadow: T.shadowSm, transition: 'all 0.22s', textAlign: 'center' }}>
                <div style={{ width: 44, height: 44, borderRadius: 13, background: T.purpleLight, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <SvgIcon name={action.icon} size={20} color={T.purple} />
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary, marginBottom: 4 }}>{action.label}</div>
                <div style={{ fontSize: 10, color: T.textMuted, lineHeight: 1.4 }}>{action.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </NmCard>

      {/* ── Row 3: Profile card + Featured models ── */}
      <div className="profile-models-grid" style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 18, alignItems: 'start' }}>

        {/* Profile card */}
        <NmCard hover={false} style={{ padding: '28px 24px', textAlign: 'center' }}>
          {/* Avatar */}
          <div style={{ width: 80, height: 80, borderRadius: '50%', margin: '0 auto 16px', boxShadow: T.shadowSm, overflow: 'hidden', background: T.bg }}>
            {userProfile?.avatar ? (
              <img src={userProfile.avatar} alt={userProfile.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${T.purple}, ${T.purpleMid})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 28, fontWeight: 800 }}>
                {(userProfile?.name || user?.email || 'U')[0].toUpperCase()}
              </div>
            )}
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.textPrimary, letterSpacing: -0.3, marginBottom: 4 }}>
            {userProfile?.name || 'User'}
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 20 }}>{user?.email}</div>

          {/* Divider */}
          <div style={{ height: 1, background: T.border, marginBottom: 20 }} />

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { label: 'Balance',   value: `${fmtDgcc(dgccData?.balance)} DGCC` },
              { label: 'Purchased', value: `${fmtDgcc(dgccData?.total_purchased)} DGCC` },
              { label: 'Spent',     value: `${fmtDgcc(dgccData?.total_spent)} DGCC` },
              { label: 'Bonus',     value: `${fmtDgcc(dgccData?.total_bonus)} DGCC` },
            ].map(s => (
              <div key={s.label} style={{ background: T.bg, borderRadius: 12, padding: '12px 10px', boxShadow: `inset 2px 2px 5px #d0d0e8, inset -2px -2px 5px rgba(255,255,255,0.65)` }}>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, color: T.textMuted, textTransform: 'uppercase', marginBottom: 5 }}>{s.label}</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: T.purple }}>{s.value}</div>
              </div>
            ))}
          </div>


        </NmCard>

        {/* Featured models */}
        <NmCard hover={false} style={{ padding: '24px 26px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, letterSpacing: -0.2 }}>Featured AI Models</div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>Available on the DAGGPT platform</div>
            </div>
            <button onClick={() => setActiveSection('models')}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 10, border: 'none', background: T.purpleLight, color: T.purple, fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.3 }}>
              View all <SvgIcon name="arrow" size={12} color={T.purple} />
            </button>
          </div>
          <div className="models-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
            {MODELS.map(model => (
              <div key={model.name}
                style={{ background: T.bg, borderRadius: 16, padding: '16px', boxShadow: T.shadowSm, cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = T.shadow; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = T.shadowSm; e.currentTarget.style.transform = 'translateY(0)'; }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: T.purpleLight, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                  <SvgIcon name={model.icon} size={16} color={T.purple} />
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary, marginBottom: 3, letterSpacing: -0.1 }}>{model.name}</div>
                <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 8 }}>{model.provider}</div>
                <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.6, color: T.purple, background: T.purpleLight, padding: '3px 8px', borderRadius: 6, textTransform: 'uppercase' }}>
                  {model.badge}
                </span>
              </div>
            ))}
          </div>
        </NmCard>
      </div>
    </div>
  );

  const renderMobileDashboardHome = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingBottom: 32 }}>
      
      {/* ── Mobile Welcome Card ── */}
      <div style={{
        background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
        borderRadius: '24px',
        padding: '24px 20px',
        color: '#FFFFFF',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 12px 30px rgba(124, 58, 237, 0.25)',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.15) 0%, transparent 50%)',
          pointerEvents: 'none',
        }} />
        
        <div style={{ display: 'flex', alignItems: 'center', justifySpaceBetween: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              opacity: 0.8,
            }}>
              Welcome Back
            </span>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 800,
              margin: '4px 0 0',
              letterSpacing: '-0.02em',
            }}>
              {userProfile?.name || user?.email?.split('@')[0] || 'User'}
            </h2>
          </div>
          
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            overflow: 'hidden',
            background: 'rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {userProfile?.avatar ? (
              <img src={userProfile.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ fontSize: '18px', fontWeight: 800 }}>
                {(userProfile?.name || user?.email || 'U')[0].toUpperCase()}
              </span>
            )}
          </div>
        </div>

        <p style={{
          fontSize: '12.5px',
          lineHeight: 1.45,
          opacity: 0.85,
          margin: 0,
          maxWidth: '85%',
        }}>
          Manage your aggregated DGCC balance, launch workspaces, and monitor multi-modal analytics on the fly.
        </p>
      </div>

      {/* ── Stat Cards 2x2 Grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        {[
          { 
            label: 'DGCC Balance', 
            val: dgccData !== null ? fmtDgcc(dgccData.balance) : '-', 
            desc: `≈ $${(+(dgccData?.balance ?? 0)).toFixed(2)} USD`,
            icon: 'dgcc', 
            onClick: () => setActiveSection('dgcc'),
            grad: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
            bg: '#FFFFFF'
          },
          { 
            label: 'Total Spent', 
            val: dgccData !== null ? fmtDgcc(dgccData.total_spent) : '-', 
            desc: 'DGCC consumed',
            icon: 'zap',
            bg: '#FFFFFF'
          },
          { 
            label: 'Account Tier', 
            val: userProfile?.isPremium ? 'Premium' : 'Free', 
            desc: 'Current active plan',
            icon: 'shield',
            bg: '#FFFFFF'
          },
          { 
            label: 'Bonus Grants', 
            val: dgccData !== null ? fmtDgcc(dgccData.total_bonus) : '-', 
            desc: 'DGCC free rewards',
            icon: 'star',
            bg: '#FFFFFF'
          }
        ].map((card, idx) => (
          <div 
            key={idx}
            onClick={card.onClick}
            style={{
              background: card.bg,
              borderRadius: '20px',
              padding: '16px',
              border: '1px solid rgba(124, 58, 237, 0.08)',
              boxShadow: '0 4px 20px rgba(124, 58, 237, 0.04)',
              cursor: card.onClick ? 'pointer' : 'default',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ 
                width: '32px', 
                height: '32px', 
                borderRadius: '99px', 
                background: card.grad ? card.grad : 'rgba(124, 58, 237, 0.07)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <SvgIcon name={card.icon} size={13} color={card.grad ? '#FFFFFF' : '#7C3AED'} strokeWidth={2} />
              </div>
              {card.onClick && <SvgIcon name="arrow" size={10} color={T.textMuted} />}
            </div>
            
            <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', color: '#71717A', textTransform: 'uppercase', marginBottom: 4 }}>
              {card.label}
            </div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#0A0A0A', letterSpacing: '-0.02em' }}>
              {card.val}
            </div>
            <div style={{ fontSize: '10.5px', color: '#A1A1AA', marginTop: 2 }}>
              {card.desc}
            </div>
          </div>
        ))}
      </div>

      {/* ── Quick Launch Ribbon ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, padding: '0 4px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 800, color: '#0A0A0A', margin: 0, letterSpacing: '-0.01em' }}>
            Quick Launch
          </h3>
          <span style={{ fontSize: '11px', color: '#71717A', fontWeight: 500 }}>Swipe →</span>
        </div>
        
        <div style={{
          display: 'flex',
          gap: '12px',
          overflowX: 'auto',
          paddingBottom: '8px',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }} className="dgw-scroll-hide">
          {QUICK_ACTIONS.map((action, i) => (
            <div 
              key={i}
              onClick={() => window.location.href = action.href}
              style={{
                flex: '0 0 140px',
                background: '#FFFFFF',
                borderRadius: '18px',
                padding: '16px 12px',
                border: '1px solid rgba(124, 58, 237, 0.08)',
                boxShadow: '0 4px 18px rgba(124, 58, 237, 0.03)',
                textAlign: 'center',
                cursor: 'pointer',
              }}
            >
              <div style={{ 
                width: '38px', 
                height: '38px', 
                borderRadius: '12px', 
                background: 'rgba(124, 58, 237, 0.08)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 10px'
              }}>
                <SvgIcon name={action.icon} size={16} color="#7C3AED" strokeWidth={2} />
              </div>
              <div style={{ fontSize: '11.5px', fontWeight: 700, color: '#0A0A0A', marginBottom: 2 }}>
                {action.label}
              </div>
              <div style={{ fontSize: '9px', color: '#A1A1AA', lineHeight: 1.2 }}>
                {action.desc.split(' ')[0]} Workspaces
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Featured Models List ── */}
      <div style={{
        background: '#FFFFFF',
        borderRadius: '24px',
        padding: '20px 18px',
        border: '1px solid rgba(124, 58, 237, 0.08)',
        boxShadow: '0 4px 20px rgba(124, 58, 237, 0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: 800, color: '#0A0A0A', margin: 0, letterSpacing: '-0.01em' }}>
              Featured AI Models
            </h3>
            <span style={{ fontSize: '10.5px', color: '#A1A1AA' }}>Optimized on DAGGPT</span>
          </div>
          <button 
            onClick={() => setActiveSection('models')}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 4, 
              padding: '6px 12px', 
              borderRadius: '10px', 
              border: 'none', 
              background: 'rgba(124, 58, 237, 0.08)', 
              color: '#7C3AED', 
              fontSize: '10.5px', 
              fontWeight: 700, 
              cursor: 'pointer' 
            }}
          >
            All <SvgIcon name="arrow" size={10} color="#7C3AED" />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {MODELS.slice(0, 4).map((model, idx) => (
            <div 
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '8px 0',
                borderBottom: idx < 3 ? '1px solid rgba(124, 58, 237, 0.04)' : 'none',
              }}
            >
              <div style={{ 
                width: '32px', 
                height: '32px', 
                borderRadius: '8px', 
                background: 'rgba(124, 58, 237, 0.06)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <SvgIcon name={model.icon} size={14} color="#7C3AED" strokeWidth={2} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12.5px', fontWeight: 700, color: '#0A0A0A' }}>
                  {model.name}
                </div>
                <div style={{ fontSize: '10px', color: '#A1A1AA', marginTop: 1 }}>
                  {model.provider}
                </div>
              </div>
              <span style={{ 
                fontSize: '8.5px', 
                fontWeight: 700, 
                color: '#7C3AED', 
                background: 'rgba(124, 58, 237, 0.08)', 
                padding: '3px 8px', 
                borderRadius: '6px', 
                textTransform: 'uppercase',
                letterSpacing: '0.04em'
              }}>
                {model.badge}
              </span>
            </div>
          ))}
        </div>
      </div>
      
    </div>
  );

  // ─── Section renderer ──────────────────────────────────────────
  const renderContent = () => {
    switch (activeSection) {
      case 'introduction': return isMobile ? renderMobileDashboardHome() : renderDashboardHome();
      case 'profile':      return <ProfileSection />;
      case 'sentinel-rent': return <SentinelNodes />;
      case 'sentinel-host': return <SentinelHost />;
      case 'sentinel-pods': return <MyPodsDashboard />;
      case 'investor-dashboard': return (userProfile?.is_investor || user?.is_investor) ? <InvestorDashboard /> : renderDashboardHome();
      case 'models':       return <ModelsPage embedded />;
      case 'rankings':     return <RankingsSection />;
      case 'dgcc':         return <CreditsSection userId={user?.id} />;
      case 'plans':        return <PlansSection userId={user?.id} />;
      case 'usage':        return <UsageSection userId={user?.id} />;
      case 'wallet':       return <WalletPage userId={user?.id} dgccData={dgccData} refreshBalance={refreshBalance} />;
      case 'gallery':      return <GallerySection userId={user?.id} />;
      default: return (
        <NmCard hover={false} style={{ padding: '60px', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: 18, margin: '0 auto 16px', background: T.bg, boxShadow: T.shadowSm, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SvgIcon name="clock" size={24} color={T.textMuted} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary, marginBottom: 6 }}>{NAV_ITEMS.find(n => n.id === activeSection)?.label}</div>
          <div style={{ fontSize: 13, color: T.textMuted }}>Coming soon</div>
        </NmCard>
      );
    }
  };


  const scaledStyle = scale !== 1 ? {
    position: 'fixed',
    top: 0,
    left: 0,
    width: `${100 / scale}vw`,
    height: `${100 / scale}vh`,
    transform: `scale(${scale})`,
    transformOrigin: 'top left',
    overflowY: 'auto',
  } : {
    width: '100%',
    minHeight: '100vh',
  };

  return (
    <div className="dashboard-layout" style={{ display: 'flex', background: T.bg, ...scaledStyle }}>
      <style>{`
        @media (max-width: 1024px) {
          .dashboard-layout {
            flex-direction: column !important;
            min-height: 100vh !important;
          }
          .dashboard-mobile-top {
            display: flex !important;
          }
          .dashboard-sidebar {
            transform: translateX(-100%);
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            border-right: 1px solid rgba(124,58,237,0.10) !important;
            height: 100dvh !important;
          }
          .dashboard-sidebar.open {
            transform: translateX(0);
          }
          .dashboard-main {
            margin-left: 0 !important;
            padding: 16px 14px !important;
            padding-top: 20px !important;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .profile-models-grid {
            grid-template-columns: 1fr !important;
          }
          .quick-actions-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
        @media (max-width: 640px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .quick-actions-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .models-grid {
            grid-template-columns: 1fr !important;
          }
        }
        .dgw-scroll-hide::-webkit-scrollbar {
          display: none !important;
        }
        .dgw-scroll-hide {
          -ms-overflow-style: none !important;
          scrollbar-width: none !important;
        }
      `}</style>
      {renderSidebar()}

      {/* Mobile Top Header */}
      <div className="dashboard-mobile-top" style={{ display: 'none', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: T.bg, borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 40, backdropFilter: 'blur(10px)', background: 'rgba(255, 255, 255, 0.85)' }}>
        <img src={mainLogo} alt="DAGGPT" style={{ height: 26 }} />
        <button onClick={() => setMobileMenuOpen(true)} style={{ background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 8, padding: 6, color: T.purple, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
      </div>

      {/* Main */}
      <main className="dashboard-main" style={{ marginLeft: 248, flex: 1, padding: '32px 36px', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 30, flexWrap: 'wrap', gap: 16 }}>
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: mounted ? 1 : 0, y: mounted ? 0 : -6 }} transition={{ duration: 0.3 }}>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: T.textPrimary, margin: 0, letterSpacing: -0.6 }}>
              {greeting},{' '}
              <span style={{ background: `linear-gradient(135deg, ${T.purple}, ${T.purpleMid})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {userProfile?.name || user?.email?.split('@')[0] || 'User'}
              </span>
            </h1>
            <p style={{ fontSize: 13, color: T.textMuted, margin: '5px 0 0', fontWeight: 500 }}>
              {currentTime ? currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '\u00A0'}
            </p>
          </motion.div>

          {/* Live clock */}
          <div style={{ background: T.bg, borderRadius: 14, padding: '10px 18px', boxShadow: T.shadowSm, display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: `${T.purple}`, boxShadow: `0 0 0 3px ${T.purpleLight}` }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
              {currentTime ? currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '\u00A0'}
            </span>
          </div>
        </div>

        {/* Page content */}
        <motion.div
          key={activeSection}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22 }}>
          {renderContent()}
        </motion.div>
      </main>


      {/* ─── Staking Perk Modal (post DGCC purchase) - Dribbble-grade premium ─── */}
      <AnimatePresence>
      {showStakingModal && (
        <>
          <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300..700&family=JetBrains+Mono:wght@400;500;600;700&display=swap');
            @keyframes spmShimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
          `}</style>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(245, 243, 238, 0.72)',
              backdropFilter: 'blur(20px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '24px',
              fontFamily: '"Inter", -apple-system, sans-serif',
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 26 }}
              style={{
                position: 'relative', width: '100%',
                maxWidth: stakingStep === 1 && !stakingDone ? '720px' : '560px',
                background: SPM.bg, borderRadius: '24px', overflow: 'hidden',
                boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 2px 4px rgba(10,10,10,0.04), 0 24px 80px -20px rgba(10,10,10,0.22)',
                border: `1px solid ${SPM.line}`,
              }}
            >
              <SpmAmbientOrbs />

              <AnimatePresence mode="wait">
                {/* ═══ SUCCESS ═══ */}
                {stakingDone && (
                  <motion.div key="done"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    style={{ position: 'relative', padding: '72px 56px', textAlign: 'center' }}>
                    <motion.div
                      initial={{ scale: 0 }} animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 220, damping: 16, delay: 0.05 }}
                      style={{ position: 'relative', width: '88px', height: '88px', margin: '0 auto 28px' }}>
                      {[0, 1].map(i => (
                        <motion.div key={i}
                          initial={{ scale: 0.4, opacity: 0.6 }}
                          animate={{ scale: 2, opacity: 0 }}
                          transition={{ duration: 1.6, delay: 0.3 + i * 0.5, repeat: Infinity }}
                          style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: SPM.accentGrad, opacity: 0.15 }} />
                      ))}
                      <div style={{
                        position: 'absolute', inset: 0, borderRadius: '50%',
                        background: SPM.accentGrad,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 12px 32px -8px rgba(16,185,129,0.5)',
                      }}>
                        <motion.svg
                          width="40" height="40" viewBox="0 0 24 24" fill="none"
                          stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <motion.polyline points="20 6 9 17 4 12"
                            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                            transition={{ duration: 0.5, delay: 0.35 }} />
                        </motion.svg>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.55 }}
                      style={{ fontFamily: SPM_MONO, fontSize: '10px', letterSpacing: '0.28em', color: SPM.ok, fontWeight: 600, marginBottom: '18px' }}>
                      ✦ ON-CHAIN · CONFIRMED
                    </motion.div>

                    <motion.h1
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.65, type: 'spring', stiffness: 180, damping: 22 }}
                      style={{ fontFamily: SPM_SERIF, fontSize: '44px', lineHeight: 1.05, color: SPM.ink, fontWeight: 400, letterSpacing: '-0.03em', margin: '0 0 14px' }}>
                      Your stake is <em style={{ fontStyle: 'italic', background: SPM.accentGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'inline-block', padding: '0 0.08em' }}>registered</em>.
                    </motion.h1>

                    <motion.p
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
                      style={{ fontSize: '15px', color: SPM.muted, lineHeight: 1.6, margin: '0 auto', maxWidth: '420px' }}>
                      <span style={{ fontFamily: SPM_MONO, color: SPM.ink, fontWeight: 600 }}>{stakingDgcc} DGCC</span> locked for{' '}
                      <span style={{ fontFamily: SPM_MONO, color: SPM.ink, fontWeight: 600 }}>{stakingSelected?.duration}Y</span> at{' '}
                      <span style={{ fontFamily: SPM_MONO, fontWeight: 700, background: SPM.accentGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        {stakingSelected?.apy}% APY
                      </span>. Rewards accrue daily on DAGChain.
                    </motion.p>
                  </motion.div>
                )}

                {/* ═══ STEP 1 ═══ */}
                {!stakingDone && stakingStep === 1 && (
                  <motion.div key="step1"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1.15fr 0.85fr' }}>
                    <div style={{ padding: '40px 36px 36px 40px' }}>
                      <motion.div
                        initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '28px' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: SPM.accentGrad }} />
                        <span style={{ fontFamily: SPM_MONO, fontSize: '10px', letterSpacing: '0.22em', color: SPM.ink, fontWeight: 600 }}>
                          DAGGPT PURCHASE · STAKING PERK
                        </span>
                      </motion.div>

                      <motion.h1
                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                        style={{ fontFamily: SPM_SERIF, fontSize: '46px', lineHeight: 1.0, color: SPM.ink, fontWeight: 400, letterSpacing: '-0.035em', margin: '0 0 18px' }}>
                        Put <em style={{ fontStyle: 'italic', background: SPM.accentGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'inline-block', padding: '0 0.08em' }}>{spmFmt(Number(stakingDgcc))}</em> DGCC<br />to work.
                      </motion.h1>

                      <motion.p
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26 }}
                        style={{ fontSize: '14px', color: SPM.muted, lineHeight: 1.65, margin: '0 0 28px', maxWidth: '380px' }}>
                        Instead of sitting idle in your balance, lock these coins into DAGChain's native staking program and earn yield until maturity.
                      </motion.p>

                      <motion.div
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }}
                        style={{ display: 'flex', gap: '10px', marginBottom: '32px', flexWrap: 'wrap' }}>
                        <div style={{ padding: '10px 14px', borderRadius: '999px', background: SPM.canvas, border: `1px solid ${SPM.line}`, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontFamily: SPM_MONO, fontSize: '10px', color: SPM.muted, letterSpacing: '0.1em' }}>STAKEABLE</span>
                          <span style={{ fontFamily: SPM_MONO, fontSize: '13px', color: SPM.ink, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            {spmFmt(Number(stakingDgcc))} DGCC
                          </span>
                        </div>
                        <div style={{ padding: '10px 14px', borderRadius: '999px', background: SPM.accentTint, border: '1px solid #A7F3D0', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontFamily: SPM_MONO, fontSize: '10px', color: '#065F46', letterSpacing: '0.1em', fontWeight: 600 }}>MAX YIELD</span>
                          <span style={{ fontFamily: SPM_MONO, fontSize: '13px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', background: SPM.accentGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                            +{spmFmt(Number(stakingDgcc) * 0.24 * 3)} DGCC
                          </span>
                        </div>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.38 }}
                        style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <motion.button
                          whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}
                          onClick={() => setStakingStep(2)}
                          style={{
                            position: 'relative', overflow: 'hidden',
                            padding: '14px 24px', borderRadius: '999px',
                            background: SPM.ink, color: SPM.bg, border: 'none',
                            fontSize: '13px', fontWeight: 600, letterSpacing: '0.02em',
                            cursor: 'pointer', fontFamily: 'inherit',
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            boxShadow: '0 4px 12px -2px rgba(10,10,10,0.2)',
                          }}>
                          <span style={{ position: 'relative', zIndex: 1 }}>Choose Duration</span>
                          <motion.span
                            animate={{ x: [0, 4, 0] }}
                            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                            style={{ position: 'relative', zIndex: 1 }}>→</motion.span>
                          <span style={{
                            position: 'absolute', top: 0, left: 0, width: '40%', height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                            animation: 'spmShimmer 2.8s infinite',
                          }} />
                        </motion.button>
                        <button
                          onClick={() => { setShowStakingModal(false); setStakingStep(1); setStakingSelected(null); }}
                          style={{ padding: '14px 18px', background: 'transparent', color: SPM.muted, border: 'none', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
                          Maybe later
                        </button>
                      </motion.div>
                    </div>

                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.2, duration: 0.5 }}
                      style={{
                        position: 'relative',
                        background: `linear-gradient(135deg, ${SPM.canvas} 0%, ${SPM.accentTint} 100%)`,
                        padding: '40px 32px',
                        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                        borderLeft: `1px solid ${SPM.line}`,
                      }}>
                      <div style={{ position: 'relative' }}>
                        <div style={{ fontFamily: SPM_MONO, fontSize: '10px', letterSpacing: '0.2em', color: SPM.muted, marginBottom: '6px', fontWeight: 600 }}>
                          YIELD PROJECTION
                        </div>
                        <div style={{ fontFamily: SPM_SERIF, fontSize: '20px', color: SPM.ink, fontWeight: 500, letterSpacing: '-0.02em' }}>
                          1Y → 3Y Lock
                        </div>
                      </div>
                      <div style={{ margin: '20px 0', position: 'relative' }}>
                        <SpmYieldCurve />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                        <span style={{ fontFamily: SPM_MONO, fontSize: '10px', color: SPM.muted, letterSpacing: '0.18em' }}>UP&nbsp;TO</span>
                        <span style={{
                          fontFamily: SPM_SERIF, fontSize: '56px', fontWeight: 400, lineHeight: 0.9,
                          letterSpacing: '-0.04em',
                          background: SPM.accentGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                        }}>
                          24<span style={{ fontSize: '28px' }}>%</span>
                        </span>
                        <span style={{ fontFamily: SPM_MONO, fontSize: '11px', color: SPM.muted, letterSpacing: '0.18em', marginLeft: '4px' }}>APY</span>
                      </div>
                    </motion.div>
                  </motion.div>
                )}

                {/* ═══ STEP 2 ═══ */}
                {!stakingDone && stakingStep === 2 && (
                  <motion.div key="step2"
                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ position: 'relative', padding: '32px 40px 32px' }}>

                    <motion.button
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
                      onClick={() => setStakingStep(1)}
                      whileHover={{ x: -2 }}
                      style={{
                        fontFamily: SPM_MONO, fontSize: '10px', letterSpacing: '0.18em',
                        color: SPM.ink, fontWeight: 600, background: 'none', border: 'none',
                        cursor: 'pointer', padding: 0, marginBottom: '18px',
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                      }}>
                      ← BACK
                    </motion.button>

                    <motion.h2
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}
                      style={{ fontFamily: SPM_SERIF, fontSize: '36px', lineHeight: 1.02, color: SPM.ink, fontWeight: 400, letterSpacing: '-0.03em', margin: '0 0 10px' }}>
                      Lock it for how <em style={{ fontStyle: 'italic', background: SPM.accentGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'inline-block', padding: '0 0.08em' }}>long</em>?
                    </motion.h2>
                    <motion.p
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
                      style={{ fontSize: '13px', color: SPM.muted, margin: '0 0 24px', lineHeight: 1.6 }}>
                      Longer = higher APY. Your <span style={{ fontFamily: SPM_MONO, color: SPM.ink, fontWeight: 600 }}>{Number(stakingDgcc).toLocaleString()} DGCC</span> stays locked on DAGChain until maturity.
                    </motion.p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                      {STAKING_OPTIONS.map((opt, i) => {
                        const isSelected = stakingSelected?.duration === opt.duration;
                        const gained = Number(stakingDgcc) * (opt.apy / 100) * opt.duration;
                        return (
                          <motion.button
                            key={opt.duration}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.26 + i * 0.06 }}
                            onClick={() => setStakingSelected(opt)}
                            whileHover={{ y: -2 }} whileTap={{ scale: 0.99 }}
                            style={{
                              position: 'relative',
                              display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: '20px',
                              padding: '18px 22px', borderRadius: '16px',
                              background: isSelected ? `linear-gradient(135deg, ${SPM.accentTint} 0%, #D1FAE5 100%)` : SPM.bg,
                              border: isSelected ? `1.5px solid ${SPM.accentA}` : `1px solid ${SPM.line}`,
                              cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                              transition: 'background 0.2s, border 0.2s',
                              boxShadow: isSelected ? '0 8px 24px -8px rgba(16,185,129,0.35)' : '0 1px 2px rgba(10,10,10,0.02)',
                            }}>
                            {isSelected && (
                              <motion.div
                                layoutId="spm-gpt-active"
                                style={{ position: 'absolute', left: 0, top: '14%', bottom: '14%', width: '3px', borderRadius: '999px', background: SPM.accentGrad }}
                                transition={{ type: 'spring', stiffness: 380, damping: 30 }} />
                            )}
                            <div>
                              <div style={{ fontFamily: SPM_SERIF, fontSize: '24px', color: SPM.ink, fontWeight: 500, letterSpacing: '-0.02em', marginBottom: '4px', display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                {opt.duration}
                                <span style={{ fontSize: '14px', color: SPM.muted, fontWeight: 400 }}>
                                  {opt.duration === 1 ? 'year' : 'years'}
                                </span>
                              </div>
                              <div style={{ fontFamily: SPM_MONO, fontSize: '11px', color: SPM.muted, letterSpacing: '0.04em' }}>
                                +{spmFmt(gained)} DGCC earned
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', minWidth: '74px' }}>
                              <div style={{
                                fontFamily: SPM_SERIF, fontSize: '32px', fontWeight: 400, lineHeight: 1,
                                letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums',
                                background: isSelected ? SPM.accentGrad : 'none',
                                WebkitBackgroundClip: isSelected ? 'text' : 'initial',
                                WebkitTextFillColor: isSelected ? 'transparent' : SPM.ink,
                                color: isSelected ? 'transparent' : SPM.ink,
                              }}>
                                {opt.apy}%
                              </div>
                              <div style={{ fontFamily: SPM_MONO, fontSize: '9px', color: SPM.muted, letterSpacing: '0.2em', marginTop: '2px' }}>
                                APY
                              </div>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>

                    <AnimatePresence>
                      {stakingSelected && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25 }}
                          style={{ overflow: 'hidden', marginBottom: '16px' }}>
                          <div style={{
                            padding: '14px 16px', background: SPM.canvas,
                            border: `1px solid ${SPM.line}`, borderRadius: '12px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                          }}>
                            <div>
                              <div style={{ fontFamily: SPM_MONO, fontSize: '9px', letterSpacing: '0.2em', color: SPM.muted, marginBottom: '2px' }}>AT MATURITY</div>
                              <div style={{ fontFamily: SPM_MONO, fontSize: '14px', color: SPM.ink, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                {spmFmt(Number(stakingDgcc) + Number(stakingDgcc) * (stakingSelected.apy / 100) * stakingSelected.duration)}
                                <span style={{ color: SPM.muted, fontWeight: 400 }}> DGCC</span>
                              </div>
                            </div>
                            <div style={{ fontFamily: SPM_MONO, fontSize: '10px', color: SPM.muted, letterSpacing: '0.08em', textAlign: 'right' }}>
                              {new Date(Date.now() + stakingSelected.duration * 365 * 24 * 60 * 60 * 1000)
                                .toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).toUpperCase()}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {stakingError && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                        style={{
                          marginBottom: '14px', padding: '10px 14px',
                          background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px',
                          fontSize: '12px', color: '#991B1B', fontFamily: SPM_MONO,
                        }}>
                        {stakingError}
                      </motion.div>
                    )}

                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <motion.button
                        whileHover={stakingSelected && !stakingSubmitting ? { y: -1 } : {}}
                        whileTap={stakingSelected && !stakingSubmitting ? { scale: 0.99 } : {}}
                        onClick={async () => {
                          if (!stakingSelected) return;
                          setStakingSubmitting(true);
                          setStakingError(null);
                          try {
                            const res = await fetch(`${API_URL}/api/billing/staking-claim`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'x-user-id': user?.id },
                              body: JSON.stringify({
                                stakingDuration: stakingSelected.duration,
                                stakingApy: stakingSelected.apy,
                                dgccAmount: stakingDgcc,
                              }),
                            });
                            const data = await res.json();
                            if (res.ok && data.success) {
                              setStakingDone(true);
                              setTimeout(() => {
                                setShowStakingModal(false);
                                setStakingStep(1);
                                setStakingSelected(null);
                                setStakingDone(false);
                              }, 3200);
                            } else {
                              setStakingError(data.error || 'Something went wrong. Please try again.');
                            }
                          } catch {
                            setStakingError('Network error. Please try again.');
                          } finally {
                            setStakingSubmitting(false);
                          }
                        }}
                        disabled={!stakingSelected || stakingSubmitting}
                        style={{
                          flex: 1, position: 'relative', overflow: 'hidden',
                          padding: '15px 24px', borderRadius: '999px',
                          background: stakingSelected ? SPM.ink : SPM.lineSoft,
                          color: stakingSelected ? SPM.bg : SPM.subtle,
                          border: 'none',
                          fontSize: '13px', fontWeight: 600, letterSpacing: '0.01em',
                          cursor: stakingSelected && !stakingSubmitting ? 'pointer' : 'not-allowed',
                          fontFamily: 'inherit',
                          boxShadow: stakingSelected ? '0 4px 14px -2px rgba(10,10,10,0.25)' : 'none',
                          opacity: stakingSubmitting ? 0.75 : 1,
                        }}>
                        <span style={{ position: 'relative', zIndex: 1 }}>
                          {stakingSubmitting
                            ? 'Submitting to DAGChain...'
                            : stakingSelected
                              ? `Stake ${Number(stakingDgcc).toLocaleString()} DGCC · ${stakingSelected.apy}% APY`
                              : 'Select a duration'}
                        </span>
                        {stakingSelected && !stakingSubmitting && (
                          <span style={{
                            position: 'absolute', top: 0, left: 0, width: '40%', height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent)',
                            animation: 'spmShimmer 2.8s infinite',
                          }} />
                        )}
                      </motion.button>
                      <button
                        onClick={() => { setShowStakingModal(false); setStakingStep(1); setStakingSelected(null); }}
                        style={{ padding: '15px 18px', background: 'transparent', color: SPM.muted, border: 'none', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
                        Skip
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </>
      )}
      </AnimatePresence>
    </div>
  );
}
